import React, { useEffect, useState } from 'react'
import * as P from "../../program-state"
import * as C from "../../controls"
import { faFacebook, faLinkedin, faTiktok } from '@fortawesome/free-brands-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMultistepCreateCampaignContext } from "./campaign_create_provider"
import {z} from "zod"
import { linkedin_bid_suggestion_schema, linkedin_bid_suggestion_t } from "../../resource/types"


export interface preview_data_t {
    file_id?: string;
    headline?: string;
    message?: string;
    link?: string;
    audience_id?: string;
    meta_budget?: number;
    meta_bid_amount?: number;
    linkedin_budget?: number;
    linkedin_bid_amount?: number;
    tiktok_budget?: number;
    tiktok_bid_amount?: number;
}


export const Preview_file_component = ({file_id} : {file_id : string | undefined}) => {
    const [ _, cs ] = P.use_authenticated_program_state()
    const [file_detail, set_file_detail] = useState<any>(null)

    const fetch_file_detail = async () => {
        const [Ok, resp] = await P.issue_json_get(P.v1api`media/files/${file_id}?direct=false`, z.any(), cs.token)
        if (Ok) {
            set_file_detail(resp)
        } else {
            console.error(resp)
        }
    }

    React.useEffect(() => {
        if (file_id === undefined) {
            return
        }
        fetch_file_detail()
    }, [file_id])

    let preview_component = null
    let preview_ad_type = "Image Ad"

    switch (file_detail) {
        case null:
            return <C.Eres_spin_placeholder className="tw-p-6"/>
        default: {
            if (file_detail?.mime_type?.startsWith("video")) {
                preview_component = (
                    <video
                        width='640'
                        height='480'
                        controls
                        >
                        <source src={P.v1api`media/files/${file_id}`} type='video/mp4' />
                    </video>
                )
                preview_ad_type = "Video Ad"
            }
            else {
                preview_component = <img src={P.v1api`media/files/${file_id}`} alt="placeholder" className="tw-mx-auto tw-mb-3" />
            }
        }
    }
    return (
        <div className="tw-relative tw-px-2">
            { preview_component }
            <div className="tw-absolute tw-left-2 tw-bottom-0 tw-bg-red-600 tw-px-2 tw-py-1 tw-text-white tw-text-xs tw-font-semibold tw-uppercase">
                {preview_ad_type}
            </div>
        </div>
    )
}


export const General_card_preview_component = ({network, preview_data} : {network : string, preview_data : preview_data_t}) => {
    let budget = 0;
    let bid_amount = 0;

    const top_bar_render = () => {
        switch (network) {
            case 'facebook':
                return <>
                    <span><FontAwesomeIcon icon={faFacebook} aria-hidden="true" className="pe-1"/></span> Facebook Preview Details
                </>
            case 'linkedin':
                return <>
                    <span><FontAwesomeIcon icon={faLinkedin} aria-hidden="true" className="pe-1"/></span> Linkedin Preview Details
                </>
            case 'tiktok':
                return <>
                    <span><FontAwesomeIcon icon={faTiktok} aria-hidden="true" className="pe-1"/></span> Tiktok Preview Details
                </>
        }
    }

    const budget_and_bid_render = () => {
        switch (network) {
            case 'facebook':
                budget = preview_data.meta_budget || 0;
                bid_amount = preview_data.meta_bid_amount || 0;
                break;
            case 'linkedin':
                budget = preview_data.linkedin_budget || 0;
                bid_amount = preview_data.linkedin_bid_amount || 0;
                break;
            case 'tiktok':
                budget = preview_data.tiktok_budget || 0;
                bid_amount = preview_data.tiktok_bid_amount || 0;
                break;
        }
    
        return (
            <>
                <h1 className='tw-text-sm'>Budget: </h1>
                <h1 className='tw-font-semibold tw-text-big-stone-900'>{budget}</h1>
                <h1 className='tw-text-sm'>Bid Amount: </h1>
                <h1 className='tw-font-semibold tw-text-big-stone-900'>{bid_amount}</h1>
            </>
        )
    }

    return (
        <C.Eres_card>
            <div className="">
                <h2 className='tw-ml-2 tw-py-2'>{top_bar_render()}</h2>
                <div className="tw-p-2">
                    <C.Eres_hline />
                </div>
                <Preview_file_component file_id={preview_data.file_id} />
                <div className="tw-p-2">
                    <C.Eres_hline />
                </div>
                <div className="tw-px-3">
                    <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center tw-mt-3">
                        <h1 className='tw-text-sm'>Headline: </h1>
                        <h1 className='tw-font-semibold tw-text-big-stone-900'>{preview_data.headline}</h1>
                        <h1 className='tw-text-sm'>Message: </h1>
                        <h1 className='tw-font-semibold tw-text-big-stone-900'>{preview_data.message}</h1>
                        <h1 className='tw-text-sm'>Link: </h1>
                        <h1 className='tw-font-semibold tw-text-big-stone-900'><a href="#" className="tw-text-blue-600 tw-hover:underline tw-dark:text-blue-500">{preview_data.link}</a></h1>
                        {budget_and_bid_render()}
                    </div>
                </div>
            </div>
        </C.Eres_card>
    )
}


type allow_network_selection = {
    facebook : boolean,
    instagram : boolean,
    linkedin : boolean,
    tiktok : boolean
}


type checkbox_t = {
    id : string,
    name : string,
    label : string,
    checked : boolean
}

export const General_preview_component = ({networks, preview_data} : { networks : string[], preview_data: preview_data_t }) => {
    const [target_networks, set_target_networks] = useState<{
        [key: string]: boolean;
    }>({
        all: true,
        facebook: true,
        instagram: true,
        linkedin: true,
        tiktok: true
    });

    const checkboxes : checkbox_t[] = [
        { id: 'checkbox-0', name: 'all', label: 'All', checked: target_networks.all },
        { id: 'checkbox-1', name: 'facebook', label: 'Facebook', checked: target_networks.facebook },
        { id: 'checkbox-2', name: 'instagram', label: 'Instagram', checked: target_networks.instagram },
        { id: 'checkbox-3', name: 'linkedin', label: 'Linkedin', checked: target_networks.linkedin },
        { id: 'checkbox-4', name: 'tiktok', label: 'Tiktok', checked: target_networks.tiktok }
    ]

    const [available_checkboxes, set_available_checkboxes] = useState<checkbox_t[]>(checkboxes);

    useEffect(() => {
        if (networks === undefined) {
            return;
        }
        const updated_target_networks: { [key: string]: boolean } = {
            all: true
        };
        for (const key of networks) {
            updated_target_networks[key] = networks.includes(key);
        }
        set_target_networks(updated_target_networks);

        const updated_available_checkboxes = checkboxes.filter((checkbox) => {
            return networks.includes(checkbox.name);
        });
        set_available_checkboxes(updated_available_checkboxes);
    }, [networks]);

    const handle_checkbox_change = (event : React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;

        if (name === "all") {
            const updated_target_networks: { [key: string]: boolean } = {};
            for (const key in target_networks) {
                updated_target_networks[key] = checked;
            }
            set_target_networks(updated_target_networks);
        } else {
            const updated_target_networks = {
                ...target_networks,
                [name]: checked
            };

            if (Object.values(updated_target_networks).every((value) => value)) {
                updated_target_networks.all = true;
            } else {
                updated_target_networks.all = false;
            }

            set_target_networks(updated_target_networks);
        }
    };

    const empty_response = () => {
        return (
            <div className="tw-flex tw-justify-center tw-h-96">
                <p className="tw-text-sm">No preview data available. Please select at least one network in order to see the preview</p>
            </div>
        )
    }

    if (networks === undefined || networks.length === 0 || preview_data === undefined) {
        return empty_response();
    }

    return (
        <div className="tw-w-full">
            <div className='tw-pl-2'>
                    <h2>Show preview on</h2>
                    <div className='tw-flex tw-space-x-2 tw-overflow-x-scroll'>
                        {checkboxes.map((checkbox) => (checkbox.name in target_networks) ? (
                            <div key={checkbox.id} className="tw-flex tw-items-center tw-mb-4">
                            <input
                                onChange={handle_checkbox_change}
                                checked={checkbox.checked}
                                id={checkbox.id}
                                name={checkbox.name}
                                type="checkbox"
                                value={checkbox.name}
                                className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded tw-focus:ring-blue-500 tw-focus:ring-2"
                            />
                            <label htmlFor={checkbox.id} className="tw-cursor-pointer tw-ms-2 tw-text-sm tw-font-medium tw-text-gray-900">
                                {checkbox.label}
                            </label>
                            </div>
                        ) : null)}
                    </div>
                </div>

                <div className='tw-w-full tw-h-screen tw-space-y-2 tw-overflow-y-auto tw-overflow-x-visible'>
                    { ((target_networks.all || target_networks.facebook) && networks.includes("meta")) && <General_card_preview_component network='facebook' preview_data={preview_data} /> }
                    { ((target_networks.all || target_networks.linkedin) && networks.includes("linkedin"))&& <General_card_preview_component network='linkedin' preview_data={preview_data} /> }
                    { ((target_networks.all || target_networks.tiktok) && networks.includes("tiktok")) && <General_card_preview_component network='tiktok' preview_data={preview_data} /> }
                </div>
        </div>
    )
}


export type placement_meta = {
    key : string,
    display : string,
    // no image_url: the placement list never rendered one, and every entry
    // pointed at a facebook-hosted icon
}


export const Placement_selection_with_preview = ({placements_meta, default_values, on_change, extra} : { placements_meta : placement_meta[], default_values : string[], on_change : (selected : string[]) => void, extra? : {[key : string] : string | null} }) => {
    const [index, set_index] = useState(0)
    const [selected, set_selected] = useState<string[]>(default_values)

    // sync internal state when default_values changes (e.g., when navigating between steps)
    useEffect(() => {
        set_selected(default_values)
    }, [JSON.stringify(default_values)])

    const handle_placement_checkbox_change = (event : React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        if (checked) {
            let new_values = [...selected, name]
            set_selected(new_values)
            on_change(new_values)
        } else {
            let new_values = selected.filter((item) => item !== name)
            set_selected(new_values)
            on_change(new_values)
        }
    };

    const text_on_click = (index : number) => {
        return () => {
            set_index(index)
        }
    }

    const is_substep_active = (id: number) => id === index ? "tw-bg-blue-100 tw-font-semibold tw-rounded" : "tw-border-gray-500" 

    const placemnet_text_display = () => {
        if (selected.length === 0) {
            return "No placements selected"
        } else {
            return selected.map((item) => {
                return placements_meta.find((i) => i.key === item)?.display
            }).join(", ")
        }
    }

    // @TODO: move the display/render logic to a separate props
    const render_dislay = (item : placement_meta) => {
        if (extra) {
            if (extra[item.key] === null) {
                return item.display
            }
            return <span>{item.display} <span className="tw-text-xs">minbid {extra[item.key]}</span></span>
        } else {
            return item.display
        }
    }

    return (
        <div className="tw-mt-3 tw-border tw-rounded tw-border tw-border-control-border">
            <C.Eres_collapse title={placemnet_text_display()} open={true}>
                <div className='tw-w-full tw-px-4 tw-py-4'>
                    <ol className="tw-list-decimal tw-list-inside tw-space-y-2">
                        {
                            placements_meta.map((item, index) => (
                                <li key={item.key} className={`tw-cursor-pointer tw-flex tw-items-center ${is_substep_active(index)}`}>
                                    <input onChange={handle_placement_checkbox_change} checked={selected.includes(item.key)}  id={`placement-${index}`} name={item.key} type="checkbox" value={item.key} className="tw-w-4 tw-h-4 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded tw-focus:ring-blue-500 tw-focus:ring-2" />
                                    <p onClick={text_on_click(index)} className="tw-cursor-pointer tw-ms-2 tw-text-sm tw-font-medium tw-text-gray-900">
                                        {render_dislay(item)}
                                    </p>
                                </li>
                            ))
                        }
                    </ol>
                </div>
            </C.Eres_collapse>
        </div>
    )
}


export interface linkedin_bid_suggestion_payload_t {
    objective : string;
    brand_id : number;
    audience_id : number;
    budget : number;
    campaign_type : string;
}


export const get_linkedin_bid_suggestion = async (token : P.authn_token, payload : linkedin_bid_suggestion_payload_t) => {
    const [ok, resp] = await P.issue_json_post(P.v1api`social/linkedin/get_budget_suggestion`, payload, linkedin_bid_suggestion_schema, token);
    if (ok) {
        return resp
    } else {
        console.log("Failed to get linkedin bid suggestion", resp)
        return null
    }
}
